import { ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import Button, { ButtonProps } from 'components/atoms/Button';
import { closeModal } from 'actions/modal';
import Icon from 'components/atoms/Icon';
import { useIsMountedRef } from 'utils/hooks';
import { Configs } from 'utils/constants';
import { useThunkDispatch } from 'utils/reduxHook';
import { isFunction } from 'utils/common';

function ModalWrapper({
  children,
  containerStyles,
  disableDismiss,
  type,
  isTransparent,
  removePadding,
  contentStyles,
  noScroll = false,
  animated = true,
  closeOnEscapeKey = false,
  closeOnBackgroundClick = false,
  onUserClose,
}: {
  children?: ReactNode;
  containerStyles?: string;
  disableDismiss?: boolean;
  isTransparent?: boolean;
  removePadding?: boolean;
  type?: 'wide' | 'full-width';
  contentStyles?: string;
  noScroll?: boolean;
  animated?: boolean;
  closeOnEscapeKey?: boolean;
  closeOnBackgroundClick?: boolean;
  onUserClose?: () => void;
}) {
  const dispatch = useThunkDispatch();

  const handleToggleModal = (isOpen: boolean) => {
    if (isOpen) {
      if (onUserClose) {
        onUserClose();
      }
      dispatch(closeModal());
    }
  };

  // close modal on escape key
  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (onUserClose) {
          onUserClose();
        }
        dispatch(closeModal());
      }
    };
    if (closeOnEscapeKey) {
      document.addEventListener('keydown', keyDownHandler);
    }
    // clean up event listener
    return () => {
      if (closeOnEscapeKey) {
        document.removeEventListener('keydown', keyDownHandler);
      }
    };
  }, []);

  useEffect(() => {
    handleToggleModal(false);
  }, []);

  return (
    <div className="modal fixed w-full h-full top-0 left-0 flex items-center justify-center z-50">
      <div
        className="modal-overlay absolute w-full h-full bg-charcoal-800/50"
        onClick={() => {
          if (closeOnBackgroundClick) {
            if (onUserClose) {
              onUserClose();
            }
            dispatch(closeModal());
          }
        }}
      />
      <div
        className={classNames(
          'modal-container relative p-2 md:p-4 w-11/12 md:max-w-3xl',
          { 'animated zoomIn animate300': animated },
          containerStyles,
        )}
        style={{
          ...(window.innerWidth < Configs.Breakpoints.Md || type === 'full-width'
            ? { width: '100vw' }
            : type === 'wide'
              ? { maxWidth: '75vw' }
              : {}),
        }}
      >
        {!disableDismiss && (
          <button
            onClick={() => handleToggleModal(true)}
            className="modal-close absolute top-0 right-0 cursor-pointer z-50 flex flex-col items-center rounded-full text-sm bg-white shadow hover:bg-glooOffWhite-300"
            data-durable-id="modalDismissButton"
            data-intercom-target="modalDismissButton"
          >
            <Icon name="x" size={25} className="text-glooMedGray p-2" />
          </button>
        )}
        <div
          className={classNames('rounded-sm shadow z-50 max-h-[95vh] md:max-h-[90vh]', {
            'bg-white': !isTransparent,
            'bg-transparent': isTransparent,
            'overflow-y-auto': !noScroll,
          })}
        >
          <div className={classNames('modal-content text-left', { 'p-5 md:p-13': !removePadding }, contentStyles)}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ModalFooter({
  className,
  hidePrimary,
  hideSecondary,
  isLoading,
  justifyButtons = 'center',
  moreButtons,
  onCancel,
  onConfirm,
  primary,
  secondary,
}: {
  className?: string;
  hidePrimary?: boolean;
  hideSecondary?: boolean;
  isLoading?: boolean;
  justifyButtons?: 'center' | 'end';
  moreButtons?: ReactNode;
  onCancel?: (...args: any[]) => void;
  onConfirm?: (...args: any[]) => void;
  primary?: Partial<ButtonProps>;
  secondary?: Partial<ButtonProps>;
}) {
  const dispatch = useThunkDispatch();
  const isMountedRef = useIsMountedRef();
  const [localIsLoading, setLocalIsLoading] = useState(false);

  const handleToggleModal = async (button: 'primary' | 'secondary') => {
    if (button === 'secondary') {
      if (isFunction(onCancel)) {
        onCancel();
      } else {
        dispatch(closeModal());
      }
    } else if (onConfirm) {
      try {
        setLocalIsLoading(true);
        await onConfirm();
      } finally {
        if (isMountedRef.current) {
          setLocalIsLoading(false);
        }
      }
    } else {
      dispatch(closeModal());
    }
  };

  return (
    <div className={classNames(`flex space-x-0 md:space-x-4 flex-row justify-${justifyButtons} pt-2`, className)}>
      {moreButtons}
      {!hideSecondary && (
        <Button
          type="tertiary"
          size="gloo-medium"
          dataDurableId="modalFooterCancelButton"
          text="Cancel"
          {...(secondary || {})}
          onClick={() => handleToggleModal('secondary')}
        />
      )}
      {!hidePrimary && (
        <Button
          isLoading={isLoading || localIsLoading}
          type="primary"
          size="gloo-medium"
          dataDurableId="modalFooterOkButton"
          text="Ok"
          {...primary}
          onClick={
            onConfirm
              ? () => onConfirm()
              : primary.onClick
                ? () => primary.onClick()
                : () => handleToggleModal('secondary')
          }
        />
      )}
    </div>
  );
}

export default ModalWrapper;
